import { useState } from 'react'
import { lumi } from '@/lib/lumi'

export interface WorkCase {
  _id: string
  reporterUserId: string
  reporterRole: 'empresa' | 'colaborador'
  reporterName?: string
  targetType: 'colaborador' | 'empresa'
  targetUserId?: string
  targetName?: string
  category: string
  title: string
  description: string
  evidenceUrls?: string[]
  status: 'abierto' | 'en_revision' | 'validado' | 'rechazado'
  resolution?: string
  reviewerUserId?: string
  resolvedAt?: string
  addedToHistory?: boolean
  creator?: string
  createdAt: string
  updatedAt: string
}

export function useCases() {
  const [cases, setCases] = useState<WorkCase[]>([])
  const [loading, setLoading] = useState(false)

  const fetchByReporter = async (reporterUserId: string) => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.cases.list({
        filter: { reporterUserId },
        sort: { createdAt: -1 },
      })
      setCases(list as WorkCase[])
    } catch (e) {
      console.error('Error al cargar casos (reportante):', e)
    } finally {
      setLoading(false)
    }
  }

  const fetchAgainstTarget = async (targetUserId: string) => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.cases.list({
        filter: { targetUserId, status: 'validado' },
        sort: { resolvedAt: -1 },
      })
      setCases(list as WorkCase[])
    } catch (e) {
      console.error('Error al cargar casos (objetivo):', e)
    } finally {
      setLoading(false)
    }
  }

  const fetchAll = async () => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.cases.list({
        sort: { createdAt: -1 },
      })
      setCases(list as WorkCase[])
    } catch (e) {
      console.error('Error al cargar casos (admin):', e)
    } finally {
      setLoading(false)
    }
  }

  const createCase = async (data: Partial<WorkCase>) => {
    const now = new Date().toISOString()
    return lumi.entities.cases.create({
      ...data,
      status: data.status || 'abierto',
      addedToHistory: false,
      creator: data.reporterUserId,
      createdAt: now,
      updatedAt: now,
    })
  }

  const updateCase = async (id: string, data: Partial<WorkCase>) => {
    return lumi.entities.cases.update(id, {
      ...data,
      updatedAt: new Date().toISOString(),
    })
  }

  const deleteCase = async (id: string) => {
    return lumi.entities.cases.delete(id)
  }

  return { cases, loading, fetchByReporter, fetchAgainstTarget, fetchAll, createCase, updateCase, deleteCase }
}
